import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
    Container, Typography, Box, Stack, Grid, Slider, CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    TrendingUp, ShowChart, Groups, Shield,
} from '@mui/icons-material';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { Surface, StatCard } from '../components/primitives';

const MotionSurface = motion(Surface);

function TrackRecord() {
    const theme = useTheme();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [monthlyInvestment, setMonthlyInvestment] = useState(1000);

    useEffect(() => {
        fetch('https://api.capitolgains.io/track-record')
            .then(res => res.json())
            .then(d => { setData(d); setLoading(false); })
            .catch(err => { console.error('Error loading track record:', err); setLoading(false); });
    }, []);

    if (loading) {
        return (
            <Container sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!data || !data.summary) {
        return (
            <Container sx={{ pt: 6 }}>
                <Typography sx={{ color: 'text.secondary' }}>Track record data unavailable.</Typography>
            </Container>
        );
    }

    const { summary, periods, consensus_power, yearly, strategy_comparisons } = data;

    // Portfolio simulation — sell after 1 year, reinvest proceeds + fresh cash
    const simulateSellReinvest = (periods, amount, returnKey) => {
        const positions = []; // {buyIndex, invested, matured, sellValue}
        let cashIn = 0;
        let cashAvailable = 0;

        for (let i = 0; i < periods.length; i++) {
            const currentDate = new Date(periods[i].date);

            // Check for matured positions (held >= 1 year)
            for (const pos of positions) {
                if (pos.matured) continue;
                const buyDate = new Date(periods[pos.buyIndex].date);
                if ((currentDate - buyDate) / 86400000 >= 365) {
                    const value = pos.invested * (1 + periods[pos.buyIndex][returnKey] / 100);
                    cashAvailable += value;
                    pos.matured = true;
                }
            }

            // Deploy fresh + matured proceeds
            cashIn += amount;
            const deploy = amount + cashAvailable;
            cashAvailable = 0;

            positions.push({ buyIndex: i, invested: deploy, matured: false });
        }

        // Final value: all positions at their 1yr return
        let finalValue = 0;
        for (const pos of positions) {
            finalValue += pos.invested * (1 + periods[pos.buyIndex][returnKey] / 100);
        }
        return { cashIn, finalValue, profit: finalValue - cashIn };
    };

    const algoSim = simulateSellReinvest(periods, monthlyInvestment, 'algo_return');
    const spySim = simulateSellReinvest(periods, monthlyInvestment, 'spy_return');
    const totalInvested = algoSim.cashIn;

    // Cumulative alpha — single line showing running excess return over SPY
    const cumulativeAlphaData = [{
        id: 'Cumulative Alpha vs SPY',
        data: periods.map(p => ({ x: p.date, y: p.cumulative_alpha })),
    }];

    // Year-by-year bars — show both raw returns AND alpha
    const yearlyBars = (yearly || []).map(y => ({
        year: y.year,
        'Capitol Gains': y.avg_algo_return,
        'S&P 500': y.avg_spy_return,
        alpha: y.avg_alpha,
        hitRate: y.hit_rate,
        periods: y.periods,
    }));

    // Consensus power bars
    const consensusBars = (consensus_power || []).map(cp => ({
        buyers: `${cp.num_buyers} ${String(cp.num_buyers) === '1' ? 'buyer' : 'buyers'}`,
        'Avg Alpha vs SPY': cp.avg_alpha,
        'Beat SPY %': cp.beat_spy_pct,
    }));

    // Nivo theme for consistent chart styling
    const nivoTheme = {
        axis: {
            domain: { line: { stroke: theme.palette.custom.border.subtle } },
            ticks: {
                line: { stroke: theme.palette.custom.border.subtle },
                text: { fill: theme.palette.text.secondary, fontSize: 11, fontFamily: theme.typography.fontFamily },
            },
            legend: { text: { fill: theme.palette.text.secondary, fontSize: 12 } },
        },
        grid: { line: { stroke: theme.palette.custom.border.subtle } },
        crosshair: { line: { stroke: theme.palette.text.muted } },
        legends: { text: { fill: theme.palette.text.secondary } },
    };

    const chartColors = [theme.palette.primary.main, theme.palette.custom.direction.sellFg];

    return (
        <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
                {/* Hero */}
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Typography variant="h1" sx={{ color: 'text.primary', mb: 1.5 }}>
                        Track Record
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 660, mx: 'auto', mb: 1.5, lineHeight: 1.7 }}>
                        Members of Congress are required to publicly disclose their stock trades.
                        We analyze these disclosures to identify which stocks the most skilled
                        traders in Congress are converging on — and historically, following
                        that signal has outperformed the S&amp;P 500.
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.muted', maxWidth: 660, mx: 'auto' }}>
                        Below is the full, unfiltered evidence: {summary.total_periods} test periods
                        from {summary.date_range}, each scored using only information that was
                        publicly available at the time. No look-ahead bias, no cherry-picking.
                    </Typography>
                </Box>

                {/* Hero stats */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={TrendingUp}
                            label="Alpha vs S&P 500"
                            value={`+${summary.alpha}%`}
                            tone="positive"
                            mono
                            tooltip="Average 1-year excess return over the S&P 500. Each period, our picks' 1-year return is compared to SPY's 1-year return over the same window."
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={Shield}
                            label="Hit Rate"
                            value={`${summary.hit_rate}%`}
                            tone="positive"
                            mono
                            tooltip="Percentage of periods where our picks' 1-year return beat the S&P 500 over the same 1-year window"
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={ShowChart}
                            label="Avg 1-Year Return"
                            value={`+${summary.avg_algo_return}%`}
                            tone="positive"
                            mono
                            tooltip="Average 1-year forward return of recommended stocks across all periods"
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={Groups}
                            label="Active Picks"
                            value={`${Math.round(summary.periods_with_picks / summary.total_periods * 100)}%`}
                            mono
                            tooltip={`The strategy produced picks in ${summary.periods_with_picks} of ${summary.total_periods} periods (every 2 weeks since 2018). When no stocks meet our quality threshold, we default to the S&P 500 — no forced picks.`}
                        />
                    </Grid>
                </Grid>

                {/* Cumulative alpha chart */}
                <MotionSurface
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    padding={3}
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 0.5 }}>
                        Cumulative Alpha vs S&amp;P 500
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        Running average excess return over the S&amp;P 500. When this line goes up, the algo is beating the market.
                    </Typography>
                    <Box sx={{ height: 400 }}>
                        <ResponsiveLine
                            data={cumulativeAlphaData}
                            margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
                            xScale={{ type: 'point' }}
                            yScale={{ type: 'linear', min: 0, max: 'auto' }}
                            curve="monotoneX"
                            enablePoints={false}
                            enableGridX={false}
                            colors={[theme.palette.success.main]}
                            lineWidth={2.5}
                            enableArea
                            areaOpacity={0.08}
                            axisBottom={{
                                tickRotation: 45,
                                tickValues: cumulativeAlphaData[0].data
                                    .filter((_, i) => i % Math.ceil(periods.length / 10) === 0)
                                    .map(d => d.x),
                            }}
                            axisLeft={{
                                format: v => `${v > 0 ? '+' : ''}${v}%`,
                                legend: 'Alpha vs SPY',
                                legendPosition: 'middle',
                                legendOffset: -50,
                            }}
                            theme={nivoTheme}
                            enableSlices="x"
                            sliceTooltip={({ slice }) => {
                                const point = slice.points[0];
                                if (!point) return null;
                                return (
                                    <Box sx={{
                                        bgcolor: 'custom.surface.elevated', p: 1.5, borderRadius: 1,
                                        border: t => `1px solid ${t.palette.custom.border.default}`,
                                    }}>
                                        <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 0.5 }}>
                                            {point.data.x}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: 'success.main', fontWeight: 700,
                                        }}>
                                            Alpha: +{point.data.y}% above S&amp;P 500
                                        </Typography>
                                    </Box>
                                );
                            }}
                        />
                    </Box>
                </MotionSurface>

                {/* Portfolio calculator with slider */}
                <MotionSurface
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    padding={3}
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 0.5 }}>
                        What would you have made?
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                        If you invested this amount every 2 weeks into our top picks since 2018,
                        selling each position after 1 year and reinvesting the proceeds + fresh capital:
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 3, maxWidth: 600 }}>
                        How it works: Every 2 weeks you buy the current top picks. After holding for 1 year,
                        you sell that position and roll the proceeds — plus your next recurring investment — into
                        whatever the algorithm is recommending that day. This simulation does not account for
                        taxes or transaction costs.
                    </Typography>

                    <Box sx={{ px: 2, mb: 3 }}>
                        <Typography variant="h2" sx={{
                            color: 'primary.light', mb: 1,
                            fontFamily: t => t.typography.fontFamilyMono,
                        }}>
                            ${monthlyInvestment.toLocaleString()} / period
                        </Typography>
                        <Slider
                            value={monthlyInvestment}
                            onChange={(_, val) => setMonthlyInvestment(val)}
                            min={100}
                            max={10000}
                            step={100}
                            sx={{ maxWidth: 500 }}
                        />
                        <Stack direction="row" justifyContent="space-between" sx={{ maxWidth: 500 }}>
                            <Typography variant="caption" sx={{ color: 'text.muted' }}>$100</Typography>
                            <Typography variant="caption" sx={{ color: 'text.muted' }}>$10,000</Typography>
                        </Stack>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Surface variant="subtle" padding={2.5}>
                                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                                    Total Cash Invested
                                </Typography>
                                <Typography sx={{
                                    fontSize: '1.5rem', fontWeight: 600,
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    color: 'text.primary',
                                }}>
                                    ${totalInvested.toLocaleString()}
                                </Typography>
                            </Surface>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Surface variant="subtle" padding={2.5}>
                                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                                    Capitol Gains Portfolio
                                </Typography>
                                <Typography sx={{
                                    fontSize: '1.5rem', fontWeight: 600,
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    color: 'success.main',
                                }}>
                                    ${Math.round(algoSim.finalValue).toLocaleString()}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'success.main' }}>
                                    +${Math.round(algoSim.profit).toLocaleString()} profit
                                    ({((algoSim.finalValue / algoSim.cashIn - 1) * 100).toFixed(0)}% ROI)
                                </Typography>
                            </Surface>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Surface variant="subtle" padding={2.5}>
                                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                                    S&amp;P 500 Portfolio
                                </Typography>
                                <Typography sx={{
                                    fontSize: '1.5rem', fontWeight: 600,
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    color: 'text.primary',
                                }}>
                                    ${Math.round(spySim.finalValue).toLocaleString()}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    +${Math.round(spySim.profit).toLocaleString()} profit
                                    ({((spySim.finalValue / spySim.cashIn - 1) * 100).toFixed(0)}% ROI)
                                </Typography>
                            </Surface>
                        </Grid>
                    </Grid>
                </MotionSurface>

                {/* Year-by-year performance */}
                <MotionSurface
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    padding={3}
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 0.5 }}>
                        Year-by-Year Performance
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        Average 1-year forward return for picks made in each year. Hover for alpha and hit rate.
                    </Typography>
                    <Box sx={{ height: 350 }}>
                        <ResponsiveBar
                            data={yearlyBars}
                            keys={['Capitol Gains', 'S&P 500']}
                            indexBy="year"
                            margin={{ top: 20, right: 130, bottom: 40, left: 55 }}
                            padding={0.25}
                            groupMode="grouped"
                            colors={chartColors}
                            borderRadius={3}
                            enableLabel={false}
                            axisLeft={{
                                format: v => `${v}%`,
                                legend: '1-Year Return',
                                legendPosition: 'middle',
                                legendOffset: -45,
                            }}
                            legends={[{
                                dataFrom: 'keys',
                                anchor: 'top-right',
                                direction: 'column',
                                translateX: 120,
                                itemWidth: 110,
                                itemHeight: 20,
                                itemsSpacing: 4,
                                symbolShape: 'square',
                                symbolSize: 10,
                                itemTextColor: theme.palette.text.secondary,
                            }]}
                            tooltip={({ indexValue }) => {
                                const row = yearlyBars.find(y => y.year === indexValue);
                                if (!row) return null;
                                const alpha = row['Capitol Gains'] - row['S&P 500'];
                                return (
                                    <Box sx={{
                                        bgcolor: 'custom.surface.elevated', p: 1.5, borderRadius: 1,
                                        border: t => `1px solid ${t.palette.custom.border.default}`,
                                        minWidth: 200,
                                    }}>
                                        <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 0.5 }}>
                                            {indexValue} ({row.periods} periods)
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: chartColors[0] }}>
                                            Capitol Gains: {row['Capitol Gains'] > 0 ? '+' : ''}{row['Capitol Gains']}%
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: chartColors[1] }}>
                                            S&amp;P 500: {row['S&P 500'] > 0 ? '+' : ''}{row['S&P 500']}%
                                        </Typography>
                                        <Box sx={{ borderTop: t => `1px solid ${t.palette.custom.border.subtle}`, mt: 0.75, pt: 0.75 }}>
                                            <Typography variant="body2" sx={{
                                                fontWeight: 700,
                                                color: alpha >= 0 ? 'success.main' : 'error.main',
                                            }}>
                                                Alpha: {alpha > 0 ? '+' : ''}{alpha.toFixed(1)}%
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.muted' }}>
                                                Hit rate: {row.hitRate}%
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            }}
                            theme={nivoTheme}
                        />
                    </Box>
                </MotionSurface>

                {/* Per-period alpha distribution — show every data point */}
                <MotionSurface
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.22 }}
                    padding={3}
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 0.5 }}>
                        Every Period, Unfiltered
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        Each bar is one semi-monthly period. The height shows how much our picks' 1-year return
                        exceeded (green) or fell short of (red) the S&amp;P 500 over the same window.
                        All {periods.filter(p => p.had_picks).length} periods shown — nothing hidden.
                    </Typography>
                    <Box sx={{ height: 300 }}>
                        <ResponsiveBar
                            data={periods.filter(p => p.had_picks).map(p => ({
                                date: p.date,
                                alpha: p.alpha,
                            }))}
                            keys={['alpha']}
                            indexBy="date"
                            margin={{ top: 10, right: 20, bottom: 50, left: 55 }}
                            padding={0.15}
                            colors={({ data }) => data.alpha >= 0
                                ? theme.palette.success.main
                                : theme.palette.error.main}
                            borderRadius={1}
                            enableLabel={false}
                            axisLeft={{
                                format: v => `${v}%`,
                                legend: 'Alpha vs SPY',
                                legendPosition: 'middle',
                                legendOffset: -45,
                            }}
                            axisBottom={{
                                tickValues: periods.filter(p => p.had_picks)
                                    .filter((_, i, arr) => i % Math.ceil(arr.length / 8) === 0)
                                    .map(p => p.date),
                                tickRotation: 45,
                            }}
                            tooltip={({ data }) => (
                                <Box sx={{
                                    bgcolor: 'custom.surface.elevated', p: 1.25, borderRadius: 1,
                                    border: t => `1px solid ${t.palette.custom.border.default}`,
                                }}>
                                    <Typography variant="caption" sx={{ color: 'text.muted', display: 'block' }}>
                                        {data.date}
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 700,
                                        color: data.alpha >= 0 ? 'success.main' : 'error.main',
                                    }}>
                                        Alpha: {data.alpha > 0 ? '+' : ''}{data.alpha}%
                                    </Typography>
                                </Box>
                            )}
                            theme={nivoTheme}
                        />
                    </Box>
                    {/* Summary stats below the distribution */}
                    {(() => {
                        const alphas = periods.filter(p => p.had_picks).map(p => p.alpha);
                        const wins = alphas.filter(a => a > 0).length;
                        const losses = alphas.filter(a => a <= 0).length;
                        const best = Math.max(...alphas);
                        const worst = Math.min(...alphas);
                        const median = [...alphas].sort((a, b) => a - b)[Math.floor(alphas.length / 2)];
                        return (
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                {[
                                    { label: 'Wins / Losses', value: `${wins} / ${losses}`, tone: 'default' },
                                    { label: 'Best Period', value: `+${best.toFixed(1)}%`, tone: 'positive' },
                                    { label: 'Worst Period', value: `${worst.toFixed(1)}%`, tone: 'negative' },
                                    { label: 'Median Alpha', value: `${median > 0 ? '+' : ''}${median.toFixed(1)}%`, tone: median >= 0 ? 'positive' : 'negative' },
                                    { label: 'Best Streak', value: `${summary.max_winning_streak || '—'} periods`, tone: 'positive' },
                                    { label: 'Worst Streak', value: `${summary.max_losing_streak || '—'} periods`, tone: 'negative' },
                                ].map((s, i) => (
                                    <Grid item xs={6} md={3} key={i}>
                                        <Surface variant="subtle" padding={1.5}>
                                            <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block' }}>
                                                {s.label}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: '1.1rem', fontWeight: 600,
                                                fontFamily: t => t.typography.fontFamilyMono,
                                                color: s.tone === 'positive' ? 'success.main'
                                                    : s.tone === 'negative' ? 'error.main'
                                                    : 'text.primary',
                                            }}>
                                                {s.value}
                                            </Typography>
                                        </Surface>
                                    </Grid>
                                ))}
                            </Grid>
                        );
                    })()}
                </MotionSurface>

                {/* Risk Profile — win/loss asymmetry */}
                {(() => {
                    const withPicks = periods.filter(p => p.had_picks);
                    if (withPicks.length === 0) return null;

                    const absReturns = withPicks.map(p => p.algo_return);
                    const alphas = withPicks.map(p => p.alpha);

                    const absWins = absReturns.filter(r => r > 0);
                    const absLosses = absReturns.filter(r => r <= 0);
                    const absWinRate = (absWins.length / absReturns.length * 100).toFixed(1);
                    const absAvgWin = absWins.length > 0 ? (absWins.reduce((a, b) => a + b, 0) / absWins.length).toFixed(1) : '0.0';
                    const absAvgLoss = absLosses.length > 0 ? (absLosses.reduce((a, b) => a + b, 0) / absLosses.length).toFixed(1) : '0.0';
                    const absExpected = (absReturns.reduce((a, b) => a + b, 0) / absReturns.length).toFixed(1);

                    const alphaWins = alphas.filter(a => a > 0);
                    const alphaLosses = alphas.filter(a => a <= 0);
                    const alphaWinRate = (alphaWins.length / alphas.length * 100).toFixed(1);
                    const alphaAvgWin = alphaWins.length > 0 ? (alphaWins.reduce((a, b) => a + b, 0) / alphaWins.length).toFixed(1) : '0.0';
                    const alphaAvgLoss = alphaLosses.length > 0 ? (alphaLosses.reduce((a, b) => a + b, 0) / alphaLosses.length).toFixed(1) : '0.0';
                    const alphaExpected = (alphas.reduce((a, b) => a + b, 0) / alphas.length).toFixed(1);

                    const fmt = (v) => {
                        const num = parseFloat(v);
                        return (num > 0 ? '+' : '') + v + '%';
                    };

                    const rows = [
                        { metric: 'Win rate', absolute: absWinRate + '%', alpha: alphaWinRate + '%', absTone: 'default', alphaTone: 'default' },
                        { metric: 'Avg win', absolute: fmt(absAvgWin), alpha: fmt(alphaAvgWin), absTone: 'positive', alphaTone: 'positive' },
                        { metric: 'Avg loss', absolute: fmt(absAvgLoss), alpha: fmt(alphaAvgLoss), absTone: 'negative', alphaTone: 'negative' },
                        { metric: 'Expected value', absolute: fmt(absExpected), alpha: fmt(alphaExpected), absTone: parseFloat(absExpected) >= 0 ? 'positive' : 'negative', alphaTone: parseFloat(alphaExpected) >= 0 ? 'positive' : 'negative' },
                    ];

                    const toneColor = (tone) =>
                        tone === 'positive' ? 'success.main'
                        : tone === 'negative' ? 'error.main'
                        : 'text.primary';

                    return (
                        <MotionSurface
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.22 }}
                            padding={3}
                            sx={{ mb: 3 }}
                        >
                            <Typography variant="h3" sx={{ color: 'text.primary', mb: 0.5 }}>
                                Win / Loss Breakdown
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
                                How often picks produce positive vs negative returns, average magnitude of each,
                                and the resulting expected value per period.
                            </Typography>

                            <Box sx={{
                                border: t => `1px solid ${t.palette.custom.border.subtle}`,
                                borderRadius: 1, overflow: 'hidden',
                            }}>
                                <Box sx={{
                                    display: 'flex', px: 2.5, py: 1,
                                    borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                                    bgcolor: 'custom.surface.subtle',
                                }}>
                                    <Typography variant="overline" sx={{ color: 'text.muted', flex: '1 0 auto' }}>&nbsp;</Typography>
                                    <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 140px', textAlign: 'right' }}>Absolute Return</Typography>
                                    <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 140px', textAlign: 'right' }}>vs S&amp;P 500</Typography>
                                </Box>
                                {rows.map((row, i) => (
                                    <Box key={i} sx={{
                                        display: 'flex', alignItems: 'center', px: 2.5, py: 1.25,
                                        borderBottom: i < rows.length - 1
                                            ? t => `1px solid ${t.palette.custom.border.subtle}` : 'none',
                                        bgcolor: row.metric === 'Expected value' ? 'rgba(99,102,241,0.06)' : 'transparent',
                                    }}>
                                        <Typography variant="body2" sx={{
                                            flex: '1 0 auto', color: 'text.secondary',
                                            fontWeight: row.metric === 'Expected value' ? 600 : 400,
                                        }}>
                                            {row.metric}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            flex: '0 0 140px', textAlign: 'right',
                                            fontFamily: t => t.typography.fontFamilyMono,
                                            fontWeight: 600,
                                            color: toneColor(row.absTone),
                                        }}>
                                            {row.absolute}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            flex: '0 0 140px', textAlign: 'right',
                                            fontFamily: t => t.typography.fontFamilyMono,
                                            fontWeight: 600,
                                            color: toneColor(row.alphaTone),
                                        }}>
                                            {row.alpha}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Typography variant="body2" sx={{
                                mt: 2, px: 1, py: 1, color: 'text.secondary', fontStyle: 'italic',
                                borderLeft: t => `3px solid ${t.palette.primary.main}`,
                            }}>
                                {parseFloat(absAvgLoss) !== 0
                                    ? `${absWinRate}% of our picks produce a positive return after 1 year, averaging ${fmt(absAvgWin)}. The other ${(100 - parseFloat(absWinRate)).toFixed(1)}% average ${fmt(absAvgLoss)}. Because the wins are larger than the losses, the expected value of any given pick is ${fmt(absExpected)}.`
                                    : `${absWinRate}% of our picks produce a positive return after 1 year.`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mt: 1.5 }}>
                                Based on {withPicks.length} periods with active recommendations. "Expected value" is the
                                probability-weighted average: (win rate × avg win) + (loss rate × avg loss).
                            </Typography>
                        </MotionSurface>
                    );
                })()}

                {/* Best & Worst Individual Picks */}
                <MotionSurface
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.23 }}
                    padding={3}
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 2 }}>
                        Best &amp; Worst Individual Picks
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="overline" sx={{ color: 'success.main', display: 'block', mb: 1 }}>
                                Best period
                            </Typography>
                            <Surface variant="subtle" padding={2}>
                                <Typography variant="body2" sx={{ color: 'text.muted', mb: 0.5 }}>
                                    {summary.best_period?.date}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '1.25rem', fontWeight: 700, color: 'success.main',
                                    fontFamily: t => t.typography.fontFamilyMono,
                                }}>
                                    +{summary.best_period?.alpha?.toFixed(1)}% alpha
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.5 }}>
                                    Picks: {summary.best_period?.tickers?.join(', ')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.muted' }}>
                                    Returned +{summary.best_period?.algo_return?.toFixed(1)}% vs SPY +{summary.best_period?.spy_return?.toFixed(1)}%
                                </Typography>
                            </Surface>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="overline" sx={{ color: 'error.main', display: 'block', mb: 1 }}>
                                Worst period
                            </Typography>
                            <Surface variant="subtle" padding={2}>
                                <Typography variant="body2" sx={{ color: 'text.muted', mb: 0.5 }}>
                                    {summary.worst_period?.date}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '1.25rem', fontWeight: 700, color: 'error.main',
                                    fontFamily: t => t.typography.fontFamilyMono,
                                }}>
                                    {summary.worst_period?.alpha?.toFixed(1)}% alpha
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.5 }}>
                                    Picks: {summary.worst_period?.tickers?.join(', ')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.muted' }}>
                                    Returned {summary.worst_period?.algo_return?.toFixed(1)}% vs SPY +{summary.worst_period?.spy_return?.toFixed(1)}%
                                </Typography>
                            </Surface>
                        </Grid>
                    </Grid>
                </MotionSurface>

                {/* Recent Picks */}
                {(() => {
                    const recentWithPicks = periods.filter(p => p.had_picks).slice(-6).reverse();
                    if (recentWithPicks.length === 0) return null;
                    return (
                        <MotionSurface
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.24 }}
                            padding={3}
                            sx={{ mb: 3 }}
                        >
                            <Typography variant="h3" sx={{ color: 'text.primary', mb: 0.5 }}>
                                Recent Picks
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                Most recent picks and their 1-year forward returns. Recent periods
                                may show projected returns based on current prices (holding period not yet complete).
                            </Typography>
                            <Box sx={{
                                border: t => `1px solid ${t.palette.custom.border.subtle}`,
                                borderRadius: 1, overflow: 'hidden',
                            }}>
                                {/* Header */}
                                <Box sx={{
                                    display: 'flex', px: 2.5, py: 1,
                                    borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                                    bgcolor: 'custom.surface.subtle',
                                }}>
                                    <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 110px' }}>Pick Date</Typography>
                                    <Typography variant="overline" sx={{ color: 'text.muted', flex: 1 }}>Stocks</Typography>
                                    <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 90px', textAlign: 'right' }}>1yr Return</Typography>
                                    <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 90px', textAlign: 'right' }}>SPY 1yr</Typography>
                                    <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 90px', textAlign: 'right' }}>Alpha</Typography>
                                </Box>
                                {recentWithPicks.map((p, i) => (
                                    <Box key={i} sx={{
                                        display: 'flex', alignItems: 'center', px: 2.5, py: 1.25,
                                        borderBottom: i < recentWithPicks.length - 1
                                            ? t => `1px solid ${t.palette.custom.border.subtle}` : 'none',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                                    }}>
                                        <Typography variant="body2" sx={{
                                            color: 'text.muted', flex: '0 0 110px',
                                            fontFamily: t => t.typography.fontFamilyMono,
                                        }}>
                                            {p.date}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: 'text.primary', flex: 1,
                                            fontFamily: t => t.typography.fontFamilyMono,
                                        }}>
                                            {p.tickers?.join(', ') || '—'}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            flex: '0 0 90px', textAlign: 'right',
                                            fontFamily: t => t.typography.fontFamilyMono,
                                            fontWeight: 600,
                                            color: p.algo_return >= 0 ? 'success.main' : 'error.main',
                                        }}>
                                            {p.algo_return > 0 ? '+' : ''}{p.algo_return}%
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            flex: '0 0 90px', textAlign: 'right',
                                            fontFamily: t => t.typography.fontFamilyMono,
                                            color: 'text.secondary',
                                        }}>
                                            {p.spy_return > 0 ? '+' : ''}{p.spy_return}%
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            flex: '0 0 90px', textAlign: 'right',
                                            fontFamily: t => t.typography.fontFamilyMono,
                                            fontWeight: 700,
                                            color: p.alpha >= 0 ? 'success.main' : 'error.main',
                                        }}>
                                            {p.alpha > 0 ? '+' : ''}{p.alpha}%
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </MotionSurface>
                    );
                })()}

                {consensusBars.length > 0 && (
                    <MotionSurface
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 }}
                        padding={3}
                        sx={{ mb: 3 }}
                    >
                        <Typography variant="h3" sx={{ color: 'text.primary', mb: 0.5 }}>
                            The Consensus Signal
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            Average alpha over S&amp;P 500 by number of Congress members buying the same stock
                        </Typography>
                        <Box sx={{ height: 320 }}>
                            <ResponsiveBar
                                data={consensusBars}
                                keys={['Avg Alpha vs SPY']}
                                indexBy="buyers"
                                margin={{ top: 20, right: 20, bottom: 60, left: 55 }}
                                padding={0.4}
                                colors={[theme.palette.primary.main]}
                                borderRadius={3}
                                enableLabel
                                label={d => `${d.value > 0 ? '+' : ''}${d.value}%`}
                                labelTextColor="#ffffff"
                                axisLeft={{
                                    format: v => `${v}%`,
                                    legend: 'Alpha vs SPY',
                                    legendPosition: 'middle',
                                    legendOffset: -45,
                                }}
                                axisBottom={{
                                    legend: 'Number of members buying',
                                    legendPosition: 'middle',
                                    legendOffset: 45,
                                }}
                                tooltip={({ indexValue }) => {
                                    const cp = (consensus_power || []).find(
                                        c => `${c.num_buyers} buyer${c.num_buyers === '1' ? '' : 's'}` === indexValue
                                    );
                                    if (!cp) return null;
                                    return (
                                        <Box sx={{
                                            bgcolor: 'custom.surface.elevated', p: 1.5, borderRadius: 1,
                                            border: t => `1px solid ${t.palette.custom.border.default}`,
                                            minWidth: 180,
                                        }}>
                                            <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 0.5 }}>
                                                {cp.num_buyers} buyer{cp.num_buyers === '1' ? '' : 's'}
                                            </Typography>
                                            <Typography variant="body2" sx={{
                                                fontWeight: 700,
                                                color: cp.avg_alpha >= 0 ? 'success.main' : 'error.main',
                                            }}>
                                                Alpha: {cp.avg_alpha > 0 ? '+' : ''}{cp.avg_alpha}%
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                Beat SPY: {cp.beat_spy_pct}%
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.muted' }}>
                                                n = {cp.count.toLocaleString()} stock-periods
                                            </Typography>
                                        </Box>
                                    );
                                }}
                                theme={nivoTheme}
                            />
                        </Box>
                        {/* Sample size labels below chart */}
                        <Stack direction="row" justifyContent="space-around" sx={{ mt: 1, px: 6 }}>
                            {(consensus_power || []).map((cp, i) => (
                                <Typography key={i} variant="caption" sx={{
                                    color: 'text.muted', textAlign: 'center',
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    fontSize: '0.6875rem',
                                }}>
                                    n={cp.count.toLocaleString()}
                                </Typography>
                            ))}
                        </Stack>
                    </MotionSurface>
                )}

                {/* The Strategy */}
                <MotionSurface
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    padding={3}
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 1 }}>
                        The Strategy
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2 }}>
                        Every stock traded by Congress is scored based on two signals that empirically
                        predict market-beating returns: <strong style={{ color: 'white' }}>trader skill</strong> and{' '}
                        <strong style={{ color: 'white' }}>consensus</strong>. The top 3 stocks scoring
                        above our threshold are selected as picks each period. When no stocks qualify,
                        we default to the S&amp;P 500.
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {[
                            { label: 'Scoring Window', value: '150 days', detail: 'Trades within the last 5 months' },
                            { label: 'Selection', value: 'Top 3', detail: 'Highest-scoring stocks above threshold' },
                            { label: 'Holding Period', value: '1 year', detail: 'Each position held for 12 months' },
                            { label: 'Rebalancing', value: 'Semi-monthly', detail: 'New picks evaluated every 2 weeks' },
                        ].map((item, i) => (
                            <Grid item xs={6} md={3} key={i}>
                                <Surface variant="subtle" padding={2}>
                                    <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block' }}>
                                        {item.label}
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: '1.1rem', fontWeight: 600, color: 'text.primary',
                                        fontFamily: t => t.typography.fontFamilyMono,
                                    }}>
                                        {item.value}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.muted' }}>
                                        {item.detail}
                                    </Typography>
                                </Surface>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Strategy comparison — why this one? */}
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600, mb: 1.5, mt: 1 }}>
                        Why this strategy?
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2 }}>
                        We tested every combination of score threshold and portfolio size.
                        Here are the top contenders — score&gt;3.0, top-3 delivers the best
                        balance of alpha and consistency:
                    </Typography>
                    <Box sx={{
                        border: t => `1px solid ${t.palette.custom.border.subtle}`,
                        borderRadius: 1, overflow: 'hidden',
                    }}>
                        {/* Header */}
                        <Box sx={{
                            display: 'flex', px: 2.5, py: 1,
                            borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                            bgcolor: 'custom.surface.subtle',
                        }}>
                            {['Strategy', 'Alpha', 'Hit Rate', 'Fires On'].map(h => (
                                <Typography key={h} variant="overline" sx={{
                                    color: 'text.muted', flex: h === 'Strategy' ? '1 0 auto' : '0 0 100px',
                                    textAlign: h === 'Strategy' ? 'left' : 'right',
                                }}>
                                    {h}
                                </Typography>
                            ))}
                        </Box>
                        {(strategy_comparisons || []).map((row, i, arr) => (
                            <Box key={i} sx={{
                                display: 'flex', alignItems: 'center', px: 2.5, py: 1.25,
                                borderBottom: i < arr.length - 1 ? t => `1px solid ${t.palette.custom.border.subtle}` : 'none',
                                bgcolor: row.selected ? 'rgba(99,102,241,0.06)' : 'transparent',
                                borderLeft: row.selected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                            }}>
                                <Typography variant="body2" sx={{
                                    flex: '1 0 auto',
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    fontWeight: row.selected ? 600 : 400,
                                    color: row.selected ? 'text.primary' : 'text.secondary',
                                }}>
                                    {row.strategy}{row.selected ? ' ←' : ''}
                                </Typography>
                                <Typography variant="body2" sx={{
                                    flex: '0 0 100px', textAlign: 'right',
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    fontWeight: 600,
                                    color: row.alpha >= 0 ? 'success.main' : 'error.main',
                                }}>
                                    {row.alpha > 0 ? '+' : ''}{row.alpha}%
                                </Typography>
                                <Typography variant="body2" sx={{
                                    flex: '0 0 100px', textAlign: 'right',
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    color: 'text.primary',
                                }}>
                                    {row.hit_rate}%
                                </Typography>
                                <Typography variant="body2" sx={{
                                    flex: '0 0 100px', textAlign: 'right',
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    color: 'text.muted',
                                }}>
                                    {row.fires_on}/{row.total_periods}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </MotionSurface>

                {/* How it works */}
                <MotionSurface
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    padding={3}
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 2 }}>
                        How the scoring algorithm works
                    </Typography>
                    <Grid container spacing={3}>
                        {[
                            {
                                title: 'Trader skill weighting',
                                text: 'Every Congress member is graded A+ through F based on their historical risk-adjusted returns. We use bootstrapped Sortino ratios with confidence intervals — a statistically rigorous measure that penalizes small sample sizes and accounts for downside risk. A skilled trader\'s buy signal carries up to 5x more weight than an unproven one.',
                            },
                            {
                                title: 'Consensus detection',
                                text: 'When 3 or more distinct members independently buy the same stock within the same window, the score is amplified. This consensus signal is one of the strongest predictors of outperformance — it suggests multiple people with access to non-public legislative insight are converging on the same opportunity.',
                            },
                            {
                                title: 'Noise filtering',
                                text: 'Trade size and recency within the window are empirically proven to add no predictive value. A $15,000 trade is no more informative than a $1,000 trade, and a trade from last week is no more informative than one from 4 months ago. The algorithm focuses exclusively on who is trading and whether others agree.',
                            },
                            {
                                title: 'Point-in-time discipline',
                                text: 'Congress members have up to 45 days to disclose trades under the STOCK Act. Our backtest only uses trades that were publicly disclosed by each test date — the same information a real investor would have had. No look-ahead bias, no hindsight cheating.',
                            },
                        ].map((item, i) => (
                            <Grid item xs={12} md={6} key={i}>
                                <Box>
                                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                        {item.text}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </MotionSurface>

                {/* How we arrived at the strategy */}
                <MotionSurface
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    padding={3}
                >
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 1 }}>
                        How we validated this
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 3 }}>
                        Every parameter in the algorithm was empirically tested — not guessed. We built a
                        hyperparameter optimization framework that sweeps each variable across a range of
                        values against {summary.total_periods} historical periods.
                    </Typography>

                    {/* Optimization results table */}
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600, mb: 1.5 }}>
                        Parameter optimization results
                    </Typography>
                    <Box sx={{
                        border: t => `1px solid ${t.palette.custom.border.subtle}`,
                        borderRadius: 1, overflow: 'hidden', mb: 3,
                    }}>
                        {[
                            { param: 'Skill multiplier scale', tested: '0.1 – 1.5 (9 values)', result: '1.5 optimal', impact: '+3.08% alpha vs 0.5', verdict: 'Adopted' },
                            { param: 'Conviction cap', tested: '0.5 – uncapped (9 values)', result: '0.5 optimal', impact: '+4.88% alpha vs 2.0', verdict: 'Adopted' },
                            { param: 'Consensus bonus', tested: '0.0 – 1.0 (7 values)', result: '0.5 optimal', impact: '+0.70% alpha vs 0.3', verdict: 'Adopted' },
                            { param: 'Lookback window', tested: '30 – 365 days (8 values)', result: '150 days optimal', impact: '+1.08% alpha vs 120d', verdict: 'Adopted' },
                            { param: 'Time decay', tested: '0.0 – 2.0 (7 values)', result: '0.0 optimal', impact: 'Flat across 0.0–0.75', verdict: 'Removed' },
                            { param: 'Trade size weighting', tested: 'On vs off', result: 'No predictive value', impact: '-6.66% alpha', verdict: 'Rejected' },
                            { param: 'Mean vs sum scoring', tested: 'On vs off', result: 'Sum dominates', impact: '-9.27% alpha', verdict: 'Rejected' },
                            { param: 'Skill weighting removal', tested: 'On vs off', result: 'Skill is essential', impact: '-4.16% alpha', verdict: 'Rejected' },
                        ].map((row, i) => (
                            <Box key={i} sx={{
                                display: 'flex', alignItems: 'center', gap: 2,
                                px: 2.5, py: 1.5,
                                borderBottom: i < 7 ? t => `1px solid ${t.palette.custom.border.subtle}` : 'none',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                            }}>
                                <Box sx={{ flex: '0 0 180px' }}>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                        {row.param}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: '0 0 170px' }}>
                                    <Typography variant="caption" sx={{
                                        color: 'text.muted',
                                        fontFamily: t => t.typography.fontFamilyMono,
                                    }}>
                                        {row.tested}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: '0 0 160px' }}>
                                    <Typography variant="caption" sx={{
                                        color: row.impact.startsWith('-') ? 'error.main' : 'success.main',
                                        fontFamily: t => t.typography.fontFamilyMono,
                                        fontWeight: 600,
                                    }}>
                                        {row.impact}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{
                                        display: 'inline-block',
                                        px: 1, py: 0.25, borderRadius: 0.5,
                                        bgcolor: row.verdict === 'Adopted'
                                            ? 'rgba(34,197,94,0.1)'
                                            : row.verdict === 'Rejected'
                                            ? 'rgba(239,68,68,0.1)'
                                            : 'rgba(148,163,184,0.1)',
                                        border: row.verdict === 'Adopted'
                                            ? '1px solid rgba(34,197,94,0.25)'
                                            : row.verdict === 'Rejected'
                                            ? '1px solid rgba(239,68,68,0.25)'
                                            : '1px solid rgba(148,163,184,0.25)',
                                    }}>
                                        <Typography variant="caption" sx={{
                                            fontWeight: 600,
                                            color: row.verdict === 'Adopted'
                                                ? 'success.main'
                                                : row.verdict === 'Rejected'
                                                ? 'error.main'
                                                : 'text.secondary',
                                        }}>
                                            {row.verdict}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600, mb: 1.5 }}>
                        Validation methodology
                    </Typography>
                    <Stack spacing={1.5}>
                        {[
                            '88 individual parameter values tested via coordinate descent, then 343 joint combinations in a grid sweep to capture parameter interactions',
                            `Walk-forward backtest: ${summary.total_periods} semi-monthly periods from ${summary.date_range}, each scored using only data available at that point in time`,
                            'Notification-date filtering ensures no look-ahead bias — trades are only visible after public disclosure, not on the transaction date',
                            'Bootstrap confidence intervals (10,000 resamples) for trader skill scores ensure small sample sizes are penalized, not rewarded',
                        ].map((item, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                <Box sx={{
                                    width: 6, height: 6, borderRadius: '50%', mt: 0.8, flexShrink: 0,
                                    bgcolor: 'primary.main',
                                }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                    {item}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </MotionSurface>

                {/* Data provenance — prove the data is real */}
                <MotionSurface
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45 }}
                    padding={3}
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 1 }}>
                        Data provenance
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2.5 }}>
                        Every data point in this system traces back to a public government filing.
                        Nothing is proprietary, estimated, or modeled — the raw data is verifiable
                        by anyone.
                    </Typography>
                    <Grid container spacing={3}>
                        {[
                            {
                                title: 'Congressional disclosures',
                                text: 'All trading data comes from mandatory STOCK Act filings submitted by members of Congress. House disclosures are published by the Clerk of the U.S. House of Representatives. Senate disclosures are published by the U.S. Senate Office of Public Records.',
                                links: [
                                    { label: 'House disclosures (clerk.house.gov)', url: 'https://disclosures-clerk.house.gov/FinancialDisclosure' },
                                    { label: 'Senate disclosures (efdsearch.senate.gov)', url: 'https://efdsearch.senate.gov/search/' },
                                ],
                            },
                            {
                                title: 'Stock prices',
                                text: 'All price data — current prices, 1-year forward returns, and S&P 500 benchmark returns — is sourced from public market data. Prices are the same regardless of provider and can be verified against any financial data source.',
                                links: [],
                            },
                            {
                                title: 'Member information',
                                text: 'Congress member details — name, party, state, committee assignments — come from the official Congress.gov API maintained by the Library of Congress.',
                                links: [
                                    { label: 'Congress.gov API (api.congress.gov)', url: 'https://api.congress.gov' },
                                ],
                            },
                            {
                                title: 'Fully reproducible',
                                text: 'The backtest uses only publicly available data as inputs. The notification date on each disclosure determines when it becomes visible to the algorithm — the same moment it becomes visible to you. Every result shown on this page can be independently verified.',
                                links: [],
                            },
                        ].map((item, i) => (
                            <Grid item xs={12} md={6} key={i}>
                                <Box>
                                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: item.links.length ? 1 : 0 }}>
                                        {item.text}
                                    </Typography>
                                    {item.links.map((link, j) => (
                                        <Typography key={j} variant="caption" sx={{ display: 'block' }}>
                                            <a
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: theme.palette.text.link }}
                                            >
                                                {link.label} ↗
                                            </a>
                                        </Typography>
                                    ))}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </MotionSurface>

                {/* Disclaimer */}
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.muted', maxWidth: 600, mx: 'auto', display: 'block', lineHeight: 1.6 }}>
                        Past performance does not guarantee future results. All data is based on historical
                        backtesting using publicly available congressional disclosure filings. This is not
                        financial advice. Individual periods can and do underperform the S&amp;P 500.
                    </Typography>
                </Box>
            </motion.div>
        </Container>
    );
}

export default TrackRecord;
